const LEGACY_SELECTED_PLAN_KEY_STORAGE = "setin.selectedPlanKey";

export const MARKETING_PLAN_INTEREST_STORAGE = "setin.marketingPlanInterest";
export const MARKETING_PLAN_INTEREST_TTL_MS = 24 * 60 * 60 * 1000;

type StoredMarketingPlanInterest = {
  planKey: string;
  capturedAt: number;
};

const PUBLIC_PLAN_KEYS = new Set(["starter", "growth", "pro"]);

function isValidPlanKey(value: string | null | undefined): value is string {
  return typeof value === "string" && PUBLIC_PLAN_KEYS.has(value.trim());
}

function readStoredMarketingPlanInterest(): StoredMarketingPlanInterest | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(MARKETING_PLAN_INTEREST_STORAGE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredMarketingPlanInterest>;
    if (!isValidPlanKey(parsed.planKey) || typeof parsed.capturedAt !== "number") {
      return null;
    }
    return {
      planKey: parsed.planKey.trim(),
      capturedAt: parsed.capturedAt,
    };
  } catch {
    return null;
  }
}

function clearLegacySelectedPlanKey() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(LEGACY_SELECTED_PLAN_KEY_STORAGE);
}

export function setMarketingPlanInterest(planKey: string | null | undefined) {
  if (typeof window === "undefined" || !isValidPlanKey(planKey)) return null;

  const stored: StoredMarketingPlanInterest = {
    planKey: planKey.trim(),
    capturedAt: Date.now(),
  };
  window.localStorage.setItem(MARKETING_PLAN_INTEREST_STORAGE, JSON.stringify(stored));
  clearLegacySelectedPlanKey();
  return stored.planKey;
}

export function getMarketingPlanInterest(): string | null {
  if (typeof window === "undefined") return null;

  const stored = readStoredMarketingPlanInterest();
  if (!stored || Date.now() - stored.capturedAt > MARKETING_PLAN_INTEREST_TTL_MS) {
    clearMarketingPlanInterest();
    return null;
  }
  return stored.planKey;
}

export function clearMarketingPlanInterest() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(MARKETING_PLAN_INTEREST_STORAGE);
}

export function syncMarketingPlanInterestFromSearchParams(searchParams: URLSearchParams) {
  const planInterest = searchParams.get("plan_interest");
  if (isValidPlanKey(planInterest)) {
    return setMarketingPlanInterest(planInterest);
  }

  const legacyPlan = searchParams.get("plan");
  if (isValidPlanKey(legacyPlan)) {
    return setMarketingPlanInterest(legacyPlan);
  }

  clearLegacySelectedPlanKey();
  return getMarketingPlanInterest();
}

export function setSelectedPlanKey(planKey: string | null | undefined) {
  setMarketingPlanInterest(planKey);
}

export function getSelectedPlanKey(): string | null {
  clearLegacySelectedPlanKey();
  return null;
}

export function clearSelectedPlanKey() {
  if (typeof window === "undefined") return;
  clearLegacySelectedPlanKey();
  clearMarketingPlanInterest();
}

export function syncSelectedPlanKeyFromSearchParams(searchParams: URLSearchParams) {
  return syncMarketingPlanInterestFromSearchParams(searchParams);
}

export function getCheckoutPlanKeyFromSearchParams(searchParams: URLSearchParams) {
  const planKey = searchParams.get("plan");
  return isValidPlanKey(planKey) ? planKey.trim() : null;
}

export function getCheckoutConfirmationPath(planKey?: string | null) {
  if (!isValidPlanKey(planKey)) {
    return "/billing";
  }
  return `/billing/checkout/confirm?plan=${encodeURIComponent(planKey.trim())}`;
}

export function getPostAuthDestination() {
  clearLegacySelectedPlanKey();
  return "/";
}

export function hasSelectedPlanKey() {
  return false;
}
